import { OnboardingUserProfile, OnboardingProgress } from '../types';
import { logger } from '../utils/logger';
import { api } from './apiClient';
import { isApiEnabled, FEATURE_FLAGS } from '../utils/featureFlags';

// localStorage keys
const ONBOARDING_COMPLETED_KEY = 'onboarding_completed';
const ONBOARDING_PROGRESS_KEY = 'onboarding_progress';

// --- API helpers ---

interface OnboardingStatusResponse {
  data: { completed: boolean };
}

/** Check onboarding status from the API. Returns null on failure. */
async function fetchOnboardingStatusFromApi(): Promise<boolean | null> {
  if (!isApiEnabled(FEATURE_FLAGS.onboarding)) return null;
  try {
    const res = await api.get<OnboardingStatusResponse>('/personalization/onboarding-status');
    return res.data.completed;
  } catch (err) {
    logger.error('Failed to fetch onboarding status from API:', err);
    return null;
  }
}

/** Mark onboarding complete on the API (best-effort, non-blocking). */
function syncOnboardingCompleteToApi(): void {
  if (!isApiEnabled(FEATURE_FLAGS.onboarding)) return;
  api.post('/personalization/onboarding-complete').catch((err) => {
    logger.error('Failed to sync onboarding completion to API:', err);
  });
}

// --- Public API ---

// Check if onboarding is completed
export async function isOnboardingCompleted(): Promise<boolean> {
  try {
    // Try API first
    const apiStatus = await fetchOnboardingStatusFromApi();
    if (apiStatus !== null) {
      // Sync localStorage with API truth
      if (apiStatus) {
        localStorage.setItem(ONBOARDING_COMPLETED_KEY, new Date().toISOString());
      }
      return apiStatus;
    }

    // Fall back to localStorage
    const completed = localStorage.getItem(ONBOARDING_COMPLETED_KEY);
    return completed !== null;
  } catch (error) {
    logger.error('Failed to check onboarding completion:', error);
    return false;
  }
}

// Synchronous localStorage-only check (for code that can't await)
export function isOnboardingCompletedSync(): boolean {
  try {
    const completed = localStorage.getItem(ONBOARDING_COMPLETED_KEY);
    return completed !== null;
  } catch (error) {
    logger.error('Failed to check onboarding completion (sync):', error);
    return false;
  }
}

// Mark onboarding as completed
export function completeOnboarding(): void {
  try {
    const timestamp = new Date().toISOString();
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, timestamp);
    // Clear progress after completion
    localStorage.removeItem(ONBOARDING_PROGRESS_KEY);
    // Dual-write: also mark complete on API (non-blocking)
    syncOnboardingCompleteToApi();
  } catch (error) {
    logger.error('Failed to complete onboarding:', error);
    throw error;
  }
}

// Save partial progress (auto-save on step change)
export function saveOnboardingProgress(step: number, data: Partial<OnboardingUserProfile>): void {
  try {
    const progress: OnboardingProgress = { step, data };
    localStorage.setItem(ONBOARDING_PROGRESS_KEY, JSON.stringify(progress));
    // Also sync the partial profile data to API (best-effort, guarded by flag)
    if (!isApiEnabled(FEATURE_FLAGS.onboarding)) return;
    api.put('/personalization', {
      dietType: data.dietType,
      healthGoals: data.healthGoals,
      faithContent: data.faithContentEnabled,
      householdSize: data.householdSize,
      cookingTime: data.cookingTimePreference,
      weeklyBudget: data.budgetLevel,
    }).catch((err) => {
      logger.error('Failed to sync onboarding progress to API:', err);
    });
  } catch (error) {
    logger.error('Failed to save onboarding progress:', error);
    throw error;
  }
}

// Load saved progress
export function loadOnboardingProgress(): OnboardingProgress | null {
  try {
    const stored = localStorage.getItem(ONBOARDING_PROGRESS_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as OnboardingProgress;
  } catch (error) {
    logger.error('Failed to load onboarding progress:', error);
    return null;
  }
}

// Clear progress (used when user explicitly restarts or abandons onboarding)
export function clearOnboardingProgress(): void {
  try {
    localStorage.removeItem(ONBOARDING_PROGRESS_KEY);
  } catch (error) {
    logger.error('Failed to clear onboarding progress:', error);
  }
}

// Reset entire onboarding (for "Reset Profile" feature)
export function resetOnboarding(): void {
  try {
    localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
    localStorage.removeItem(ONBOARDING_PROGRESS_KEY);
    localStorage.removeItem('user_profile');
    localStorage.removeItem('care_recipient_profiles');
    localStorage.removeItem('household_profile');
    localStorage.removeItem('active_profile_id');
  } catch (error) {
    logger.error('Failed to reset onboarding:', error);
    throw error;
  }
}
